import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import ReferrerRegister from "@/pages/ReferrerRegister";
import SignProtocol from "@/pages/SignProtocol";
import Vacancies from "@/pages/Vacancies";
import VacancyDetail from "@/pages/VacancyDetail";
import RecommendCandidate from "@/pages/RecommendCandidate";
import PublicVacancy from "@/pages/PublicVacancy";
import PublicApply from "@/pages/PublicApply";
import Notifications from "@/pages/Notifications";
import AdminHome from "@/pages/admin/AdminHome";
import AdminVacancies from "@/pages/admin/AdminVacancies";
import AdminReferrers from "@/pages/admin/AdminReferrers";
import AdminCandidates from "@/pages/admin/AdminCandidates";
import AdminBonuses from "@/pages/admin/AdminBonuses";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminGroups from "@/pages/admin/AdminGroups";
import AdminInvitations from "@/pages/admin/AdminInvitations";
import AdminDataReset from "@/pages/admin/AdminDataReset";
import AdminUsers from "@/pages/admin/AdminUsers";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AppShell from "@/components/AppShell";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bone)]">
        <div className="animate-pulse text-[var(--forest)] font-display">A carregar…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

// Guards routes that require a completed referrer profile + signed protocol.
// Admin passes through. New referrers are redirected to onboarding.
function RequireReferrer({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, ref: null });

  useEffect(() => {
    if (user?.role === "admin") { setState({ loading: false, ref: { admin: true } }); return; }
    let cancelled = false;
    api.get("/referrers/me")
      .then(({ data }) => { if (!cancelled) setState({ loading: false, ref: data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, ref: null }); });
    return () => { cancelled = true; };
  }, [user?.role]);

  if (state.loading) {
    return <div className="text-[var(--ink-soft)]">A carregar…</div>;
  }
  if (state.ref?.admin) return children;
  if (!state.ref) return <Navigate to="/onboarding/profile" replace />;
  if (!state.ref.protocol_signed) return <Navigate to="/onboarding/protocol" replace />;
  if (state.ref.is_blocked) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect callback synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/v/:shareCode" element={<PublicVacancy />} />
      <Route path="/apply/:shareCode" element={<PublicApply />} />

      <Route element={<ProtectedRoute><AppShell><Outlet /></AppShell></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/onboarding/profile" element={<ReferrerRegister />} />
        <Route path="/onboarding/protocol" element={<SignProtocol />} />
        <Route path="/vacancies" element={<RequireReferrer><Vacancies /></RequireReferrer>} />
        <Route path="/vacancies/:id" element={<RequireReferrer><VacancyDetail /></RequireReferrer>} />
        <Route path="/recommend/:vacancyId" element={<RequireReferrer><RecommendCandidate /></RequireReferrer>} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>

      <Route element={<ProtectedRoute adminOnly><AppShell admin><Outlet /></AppShell></ProtectedRoute>}>
        <Route path="/admin" element={<AdminHome />} />
        <Route path="/admin/vacancies" element={<AdminVacancies />} />
        <Route path="/admin/referrers" element={<AdminReferrers />} />
        <Route path="/admin/candidates" element={<AdminCandidates />} />
        <Route path="/admin/bonuses" element={<AdminBonuses />} />
        <Route path="/admin/groups" element={<AdminGroups />} />
        <Route path="/admin/invitations" element={<AdminInvitations />} />
        <Route path="/admin/config" element={<AdminConfig />} />
        <Route path="/admin/reset" element={<AdminDataReset />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
